import { useCallback } from 'react';

import { BFFConsumerEnum } from '../../constants';
import { atiArrayToParams } from '../utils/text';

import { useCache } from './useCache';
import { useConsumer } from './useConsumer';
import useOriginUrl from './useOriginUrl';

type BuildURLProps = {
	baseUrl?: string;
};

interface BuildProductShortcutURLProps {
	baseUrl?: string;
}

interface BuildUCRecommendationURLProps {
	excludeAtis?: string[];
	locale?: string;
	nativeAtis?: string[];
	orgId: string;
	preferredAtis?: string[];
	product: string;
	subProduct?: string;
	userId: string;
}

interface BuildUCMenuItemsURLProps {
	ap?: string;
	d: string;
	locale?: string;
	location?: string;
	orgId: string;
	product?: string;
	ro?: string;
	rt?: string;
	subProduct?: string;
	userId?: string;
}

export enum BFFEndpointsEnum {
	AVAILABLE_PRODUCTS = '/gateway/api/app-switcher/api/available-products',
	SUGGESTIONS = '/gateway/api/app-switcher/api/universal-create/v1/suggestions',
	MENU_ITEMS_V2 = '/gateway/api/app-switcher/api/universal-create/v2/menu-items',
}

interface UseURLBuilderProps {
	cloudId?: string;
	product?: string;
	userId?: string;
}

export function useURLBuilder({ cloudId, userId, product }: UseURLBuilderProps) {
	const { consumer } = useConsumer();
	const { items: cachedItems } = useCache();
	const originUrl = useOriginUrl();

	const buildUCRecommendationURL = useCallback(
		({
			product,
			orgId,
			userId,
			locale,
			subProduct,
			nativeAtis,
			excludeAtis,
			preferredAtis,
		}: BuildUCRecommendationURLProps): string => {
			const baseUrl = BFFEndpointsEnum.SUGGESTIONS;

			const url = new URL(`${getOriginUrl({ originUrl })}${baseUrl}`);
			const searchParams = new URLSearchParams();

			const queryParams: Record<string, string | undefined | null> = {
				tenantid: cloudId,
				product,
				orgid: orgId,
				userid: userId,
				source: consumer,
				locale,
				subproduct: subProduct,
				nativeatis: atiArrayToParams(nativeAtis),
				excludeatis: atiArrayToParams(excludeAtis),
				preferredatis: atiArrayToParams(preferredAtis),
			};

			Object.entries(queryParams).forEach(([key, value]) => {
				if (value) {
					searchParams.append(key, value);
				}
			});

			// Append search params to URL
			url.search = searchParams.toString();

			return url.toString();
		},
		[consumer, cloudId, originUrl],
	);

	const buildUCMenuItemsURL = useCallback(
		({
			product: productParam,
			orgId,
			userId: userIdParam,
			locale,
			subProduct,
			d,
			ro,
			rt,
			ap,
			location,
		}: BuildUCMenuItemsURLProps): string => {
			const baseUrl = BFFEndpointsEnum.MENU_ITEMS_V2;

			const url = new URL(`${getOriginUrl({ originUrl })}${baseUrl}`);
			const searchParams = new URLSearchParams();

			// Maintain specific order: tenantid, product, orgid, userid, source, then other params
			const queryParams: Array<[string, string | undefined | null]> = [
				['tenantid', cloudId],
				['product', productParam || product],
				['orgid', orgId],
				['userid', userIdParam || userId],
				['source', consumer],
				['locale', locale],
				['subproduct', subProduct],
				['d', d],
				['ro', ro],
				['rt', rt],
				['ap', ap],
				['location', location],
			];

			queryParams.forEach(([key, value]) => {
				if (value) {
					searchParams.append(key, value);
				}
			});

			// Append search params to URL
			url.search = searchParams.toString();

			return url.toString();
		},
		[cloudId, product, userId, consumer, originUrl],
	);

	const buildProductShortcutURL = useCallback(
		({ baseUrl }: BuildProductShortcutURLProps): string => {
			baseUrl = baseUrl ?? BFFEndpointsEnum.AVAILABLE_PRODUCTS;

			// No point in building a URL if there are no items to fetch and the cache is already populated
			if (cachedItems.length > 0) {
				return '';
			}

			const queryValues = '';

			return `${getOriginUrl({ originUrl })}${baseUrl}?${queryValues}`;
		},
		[cachedItems, originUrl],
	);

	const buildURL = useCallback(
		({ baseUrl }: BuildURLProps): string => {
			let url = '';

			if (baseUrl) {
				url = `${getOriginUrl({ originUrl })}${baseUrl}`;
			}

			// Consumer specific URL building implementations happen here
			switch (consumer) {
				case BFFConsumerEnum.GLOBAL_SHORTCUTS:
					url = buildProductShortcutURL({});
					break;
				case BFFConsumerEnum.GLOBAL_SIDE_NAVIGATION:
					url = buildProductShortcutURL({});
					break;
			}

			if (!url) {
				return url;
			}

			// Append the consumer to the URL
			const formattedUrl = new URL(url);
			const updatedParams = new URLSearchParams(formattedUrl.search);

			if (consumer) {
				updatedParams.append('source', consumer);
			}

			if (cloudId) {
				updatedParams.append('tenantid', cloudId);
			}

			if (userId) {
				updatedParams.append('userid', userId);
			}

			if (product) {
				updatedParams.append('product', product);
			}

			return `${formattedUrl.origin}${formattedUrl.pathname}?${updatedParams}`;
		},
		[buildProductShortcutURL, consumer, cloudId, userId, product, originUrl],
	);

	const getOriginUrl = ({ originUrl }: { originUrl?: string } = {}): string =>
		originUrl || new URL(window.location.href).origin;

	return {
		buildURL,
		buildProductShortcutURL,
		buildUCRecommendationURL,
		buildUCMenuItemsURL,
	};
}
